import React from 'react';
import { Dialog, DialogContent, DialogActions, Button, Box } from '@mui/material';
// Flight icon removed - no longer displaying flight information
import { MaintenanceStatus, MaintenanceRecord } from '../../../../../types/api';
// ResolutionDialog removed - notes handled separately
import { MaintenanceRecordForm } from './';
import { useMaintenanceRecordEditState, useMaintenanceRecordOperations } from '../hooks';
import { useAuthV2 } from '../../../../../contexts/AuthContextV2';
import { canManageMaintenance as checkCanManageMaintenance } from '../../../../../config/roles';

interface ViewMaintenanceRecordDialogProps {
  open: boolean;
  selectedMaintenance: MaintenanceRecord | null;
  onClose: () => void;
  droneSerial: string;
  onDataUpdated?: () => void;
}

export const ViewMaintenanceRecordDialog: React.FC<ViewMaintenanceRecordDialogProps> = ({
  open,
  selectedMaintenance,
  onClose,
  droneSerial,
  onDataUpdated,
}) => {
  // Resolution notes removed - notes handled separately
  const { user, profile } = useAuthV2();

  // Check if user can manage maintenance records (admin or maintenance role)
  const canManageMaintenance = checkCanManageMaintenance(profile?.organization?.roles);

  // Use custom hooks for state management
  const state = useMaintenanceRecordEditState(selectedMaintenance, open);
  const operations = useMaintenanceRecordOperations(droneSerial, onDataUpdated);

  // Note selection removed - notes handled separately

  // Handle save operations with proper parameters
  const handleSaveMaintenanceEdit = (recordId: string) => {
    operations.handleSaveMaintenanceEdit(
      recordId,
      state.editingStatus as MaintenanceStatus,
      state.editingStartDate,
      state.editingEndDate,
      state.editingContent,
      state.setLoading,
      state.setLocalMaintenanceUpdates,
      state.handleCancelEdit
    );
  };

  // Note editing removed - notes handled separately

  const handleQualityCheckedToggle = (recordId: string, checked: boolean) => {
    operations.handleQualityCheckedToggle(recordId, checked, state.setLocalMaintenanceUpdates);
  };

  const handleStatusToggle = (recordId: string, newStatus: MaintenanceStatus) => {
    operations.handleStatusToggle(recordId, newStatus, state.setLocalMaintenanceUpdates);
  };

  const handleDeleteMaintenance = () => {
    if (selectedMaintenance?.id) {
      operations.handleDeleteMaintenance(selectedMaintenance.id, onClose, state.setLoading);
    }
  };

  // Note handlers removed - notes handled separately

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ pt: 3 }}>
          {selectedMaintenance && (
            <Box>
              {/* Flight information removed - not needed for maintenance records */}

              {/* Notes section removed - notes handled separately */}

              {/* Maintenance Records Section */}
              <MaintenanceRecordForm
                record={state.displayMaintenance || selectedMaintenance}
                editingMaintenanceId={state.editingMaintenanceId}
                editingStartDate={state.editingStartDate}
                editingEndDate={state.editingEndDate}
                editingContent={state.editingContent}
                loading={state.loading}
                currentUserId={user?.id}
                canEdit={canManageMaintenance}
                onEditMaintenance={state.handleEditMaintenance}
                onSaveMaintenanceEdit={handleSaveMaintenanceEdit}
                onCancelEdit={state.handleCancelEdit}
                onQualityCheckedToggle={handleQualityCheckedToggle}
                onStatusToggle={handleStatusToggle}
                onEditingStartDateChange={state.setEditingStartDate}
                onEditingEndDateChange={state.setEditingEndDate}
                onEditingContentChange={state.setEditingContent}
              />
            </Box>
          )}
        </DialogContent>

        {/* Dialog Actions */}
        <DialogActions sx={{ px: 3, pb: 3 }}>
          {canManageMaintenance && selectedMaintenance && !selectedMaintenance.quality_checked && (
            <Button
              variant="outlined"
              color="error"
              onClick={handleDeleteMaintenance}
              disabled={state.loading}
            >
              Delete Maintenance
            </Button>
          )}
          <Button onClick={onClose} sx={{ ml: 'auto' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resolution dialog removed - notes handled separately */}
    </>
  );
};
